/**
 * Standardized tour availability from GET /tour-times.
 *
 * PMS-agnostic replacement for the ApartmentSync Yardi/Entrata adapters.
 * The scheduler UI opts in via the `use_leads_tour_times` content token.
 */

import { isoDateTimeToDisplayTime, isoToApiDate, sortTimeSlots } from './date-utils';

export const KNOWN_TOUR_TYPES = [
  'guided_tour',
  'self_guided_tour',
  'live_video_tour',
  'virtual_tour',
] as const;

export type KnownTourType = (typeof KNOWN_TOUR_TYPES)[number];

const KNOWN_TOUR_TYPE_SET = new Set<string>(KNOWN_TOUR_TYPES);

const PMS_LEAD_ROUTER: Record<string, 'RENT_CAFE' | 'ENTRATA' | 'KNOCK'> = {
  yardi: 'RENT_CAFE',
  entrata: 'ENTRATA',
  knock: 'KNOCK',
};

export interface TourTimesSlot {
  startTime: string;
  endTime: string | null;
  tourType: string | null;
}

export interface TourTimesDay {
  date: string;
  dayOfWeek: string;
  slots: TourTimesSlot[];
}

export interface TourTimesResponse {
  propertyId: string;
  pms: string;
  timezone: string | null;
  days: TourTimesDay[];
}

export function getLeadsDomain(): string {
  const apiUrl = (import.meta as { env?: { MULTIHUB_API_URL?: string } }).env?.MULTIHUB_API_URL || '';
  if (apiUrl.includes('localhost') || apiUrl.includes('data-testing')) {
    return 'https://testing.leads.multihub.io';
  }
  if (apiUrl.includes('data-dev')) {
    return 'https://dev.leads.multihub.io';
  }
  return 'https://leads.multihub.io';
}

export function tourTimesUrl(propertyId: string, domain = getLeadsDomain()): string {
  return `${domain}/tour-times?propertyId=${encodeURIComponent(propertyId)}`;
}

export function leadRouterForPms(pms: string | null | undefined): 'RENT_CAFE' | 'ENTRATA' | 'KNOCK' | null {
  if (!pms) return null;
  return PMS_LEAD_ROUTER[pms] ?? null;
}

function isKnownTourType(value: string | null | undefined): value is KnownTourType {
  return !!value && KNOWN_TOUR_TYPE_SET.has(value);
}

function daysHaveKnownTourTypes(days: TourTimesDay[]): boolean {
  return days.some((day) => day.slots.some((slot) => isKnownTourType(slot.tourType)));
}

/**
 * A selected tour type only matches slots tagged with that type. Untyped slots
 * are included only when the payload has no known types (fallback to guided).
 */
function slotMatchesTourType(
  slot: TourTimesSlot,
  tourType: string | null,
  hasKnownTypes: boolean,
): boolean {
  if (!tourType) return true;
  if (!slot.tourType) return !hasKnownTypes;
  return slot.tourType === tourType;
}

export function tourTypesFromDays(days: TourTimesDay[]): string[] {
  const found = new Set<string>();
  let hasSlots = false;
  for (const day of days) {
    for (const slot of day.slots) {
      hasSlots = true;
      if (isKnownTourType(slot.tourType)) found.add(slot.tourType);
    }
  }
  if (found.size === 0) return hasSlots ? ['guided_tour'] : [];
  return KNOWN_TOUR_TYPES.filter((key) => found.has(key));
}

/**
 * Card order for the tour-type step: Guided, Self-Guided, Live Video, Virtual.
 * Only types that exist in the payload (or as a configured link) are included.
 */
export function orderedAvailableTourTypes(
  apiTypes: string[],
  extras: {
    selfGuidedTourLink?: string | null;
    virtualTourLink?: string | null;
    disabled?: Partial<Record<KnownTourType, boolean>>;
  } = {},
): KnownTourType[] {
  const fromApi = new Set(apiTypes);
  return KNOWN_TOUR_TYPES.filter((key) => {
    if (extras.disabled?.[key]) return false;
    if (key === 'self_guided_tour') return fromApi.has(key) || Boolean(extras.selfGuidedTourLink);
    if (key === 'virtual_tour') return fromApi.has(key) || Boolean(extras.virtualTourLink);
    return fromApi.has(key);
  });
}

/**
 * Dates with at least one displayable time for the selected type, in the
 * calendar's MM/DD/YYYY format. Uses timesForDate so highlighted days never
 * open to an empty time list.
 */
export function datesForTourType(days: TourTimesDay[], tourType: string | null): string[] {
  const dates: string[] = [];
  for (const day of days) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day.date)) continue;
    if (timesForDate(days, day.date, tourType).length > 0) dates.push(isoToApiDate(day.date));
  }
  return dates;
}

export function timesForDate(
  days: TourTimesDay[],
  isoDate: string,
  tourType: string | null,
): string[] {
  const day = days.find((d) => d.date === isoDate);
  if (!day) return [];

  const hasKnownTypes = daysHaveKnownTourTypes(days);
  const times = new Set<string>();
  for (const slot of day.slots) {
    if (!slotMatchesTourType(slot, tourType, hasKnownTypes)) continue;
    const display = isoDateTimeToDisplayTime(slot.startTime);
    if (display) times.add(display);
  }
  return sortTimeSlots(Array.from(times));
}

export async function fetchTourTimes(propertyId: string): Promise<TourTimesResponse | null> {
  if (!propertyId) return null;

  try {
    const res = await fetch(tourTimesUrl(propertyId));
    if (!res.ok) {
      console.error('[LeadsTourTimes] Failed to fetch tour times:', res.status);
      return null;
    }
    const data = await res.json();
    if (!Array.isArray(data?.days)) return null;
    return data as TourTimesResponse;
  } catch (err) {
    console.error('[LeadsTourTimes] Failed to fetch tour times:', err);
    return null;
  }
}
